/** GxTerm (screens §1.5): a label with a glossary entry gets a dotted underline and a 292 px popover; no entry, plain text. */
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { GlossaryTerm } from "@bsim/domain";
import { useCoach } from "../coach/CoachDrawer";
import { GxPopover, useSheetMode } from "./Popover";
import { useKpiComputed } from "./KpiInputs";
import type { Computed } from "../format/formulas";

const GlossaryContext = createContext<readonly GlossaryTerm[]>([]);
export const GlossaryProvider = GlossaryContext.Provider;

/** The whole glossary, for copy built from several terms (the look-through lines). */
export const useGlossary = (): readonly GlossaryTerm[] => useContext(GlossaryContext);

/** The glossary entry for a term id or KPI id, for copy that quotes it (a limit row's one-line explanation). */
export const useGlossaryEntry = (term: string): GlossaryTerm | undefined => useContext(GlossaryContext).find((g) => g.termId === term || g.kpi === term);

/** A KPI's label as a glossary term: `kpi` is the KPI id the entry explains (a KPI without an entry reads as plain text). */
export const GxKpiLabel = ({ kpi, children }: { readonly kpi: string; readonly children: ReactNode }) => <GxTerm term={kpi}>{children}</GxTerm>;

/** Acronyms a free-text label or card narrative may carry, each to the glossary entry that explains it (CET1 is left to its KPI label: reported or marked). */
const ACRONYM_TERMS: Readonly<Record<string, string>> = { NII: "nii", NIM: "nim", EVE: "eve", NCO: "nco-rate", AFS: "afs-htm", HTM: "afs-htm", FHLB: "fhlb", ECR: "ecr", ROTCE: "rotce", NDFI: "ndfi", CRE: "cre", "C&I": "ci", LTV: "ltv", bp: "bp", HVCRE: "hvcre", MMDA: "mmda" };
/** Phrases (either case of the first letter) and the entry that explains each. */
const PHRASE_TERMS: readonly (readonly [pattern: string, termId: string])[] = [
  ["[Bb]ear-flatten\\w*", "bear-flattening"], ["[Cc]redit box", "credit-box"], ["[Dd]ouble-pledg\\w*", "double-pledging"],
  ["[Pp]articipations?", "loan-participation"], ["[Ss]tatus-quo twin", "status-quo-twin"], ["[Ss]tanding policy", "standing-policy"], ["[Tt]wins?", "status-quo-twin"], ["[Ll]ook-through", "look-through"], ["[Ll]oan-to-value", "ltv"], ["[Ss]ubstandard", "substandard"],
  ["Risk Steward (?:award|badge)", "risk-steward-badge"], ["[Mm]anagement rating", "management-rating"], ["[Bb]reach(?:es)?", "breach"],
  ["[Cc]apital conservation buffer", "conservation-buffer"], ["[Cc]onservation buffer", "conservation-buffer"], ["[Dd]eposit betas?", "deposit-beta"], ["[Rr]elationship managers?", "relationship-manager"],
];
const WORDS = new RegExp(`\\b(?:${[...Object.keys(ACRONYM_TERMS), ...PHRASE_TERMS.map(([pattern]) => pattern)].join("|")})\\b`, "g");
const termOf = (word: string): string => ACRONYM_TERMS[word] ?? PHRASE_TERMS.find(([pattern]) => new RegExp(`^${pattern}$`).test(word))?.[1] ?? word;

/** A text label or paragraph with each known word (NII, EVE, NCO, AFS, HTM, FHLB, CRE, C&I, NDFI, bp, twin, credit box, bear-flattens…) given its glossary popover. */
export function GxTermText({ children }: { readonly children: string }) {
  const parts: ReactNode[] = [];
  let at = 0;
  for (const m of children.matchAll(WORDS)) {
    parts.push(children.slice(at, m.index), <GxTerm key={m.index} term={termOf(m[0])}>{m[0]}</GxTerm>);
    at = m.index + m[0].length;
  }
  parts.push(children.slice(at));
  return <>{parts}</>;
}

/** What a popover says: a glossary entry, or just a name and one plain line (a lever's catalog description). */
type TermEntry = Pick<GlossaryTerm, "label" | "plain"> & Partial<Pick<GlossaryTerm, "whyItMatters" | "conceptCardId">>;

/** `term` is a glossary `termId` or the KPI id the entry explains. */
export function GxTerm({ term, children }: { readonly term: string; readonly children: ReactNode }) {
  const entry = useGlossaryEntry(term);
  const computed = useKpiComputed(entry?.kpi);
  return entry === undefined ? <>{children}</> : <TermPopover entry={entry} {...(computed === undefined ? {} : { computed })}>{children}</TermPopover>;
}

/** A name given its own description as the popover (a lever title: the catalog's description of the lever); no description, plain text. */
export const GxDescribed = ({ label, plain, whyItMatters, conceptCardId, children }: { readonly label: string; readonly plain: string; readonly whyItMatters?: string; readonly conceptCardId?: string; readonly children: ReactNode }) =>
  plain === "" ? <>{children}</> : <TermPopover entry={{ label, plain, ...(whyItMatters === undefined ? {} : { whyItMatters }), ...(conceptCardId === undefined ? {} : { conceptCardId }) }}>{children}</TermPopover>;

/** `computed`: a KPI's formula in words and with the page's numbers ("How it's computed today"). */
function TermPopover({ entry, computed, children }: { readonly entry: TermEntry; readonly computed?: Computed; readonly children: ReactNode }) {
  const coach = useCoach();
  const conceptCardId = entry.conceptCardId;
  // Hover shows it; a click, Enter or Space (or keyboard focus) pins it open until Escape, a click away or the mouse leaves after a click elsewhere.
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || pinned;
  // Opening the coach drawer puts every page popover away: the drawer is the one thing on screen.
  useEffect(() => { setHover(false); setPinned(false); }, [coach.opens]);
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  // As a sheet the card takes focus and is closed by its own controls: hover, focus and blur no longer pin it.
  const sheet = useSheetMode();
  return (
    <span ref={anchor} onMouseEnter={() => { if (!sheet) setHover(true); }} onMouseLeave={() => setHover(false)}>
      <span className="gx-term" tabIndex={0} aria-describedby={open ? id : undefined}
        onFocus={() => { if (!sheet) setPinned(true); }} onBlur={() => { if (!sheet) setPinned(false); }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setPinned(false); setHover(false); }
          else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPinned(true); }
        }}
        onClick={() => setPinned(true)}>
        {children}
      </span>
      {open ? (
        <GxPopover anchor={anchor} id={id} label={`Glossary: ${entry.label}`} title={entry.label} passive={!pinned} returnFocus={false} onClose={() => { setPinned(false); setHover(false); }}>
          <b className="gx-pop-title">{entry.label}</b>
          <p>{entry.plain}</p>
          {entry.whyItMatters === undefined ? null : <span className="gx-why"><span>Why it matters</span>{entry.whyItMatters}</span>}
          {computed === undefined ? null : (
            <span className="gx-why" data-testid="term.computed"><span>How it's computed today</span>{computed.formula}
              <b className="gx-num">{computed.withInputs === computed.result ? computed.result : `${computed.withInputs} = ${computed.result}`}</b></span>
          )}
          {conceptCardId === undefined ? null : (
            <button type="button" className="gx-linkrow" onClick={() => coach.open({ screen: "concept", label: entry.label, conceptId: conceptCardId })}>Concept card · {entry.label}</button>
          )}
        </GxPopover>
      ) : null}
    </span>
  );
}

const GLOSS_SEEN = "bsim.gloss.seen";
const glossSeen = (): ReadonlySet<string> => { try { return new Set(JSON.parse(localStorage.getItem(GLOSS_SEEN) ?? "[]") as string[]); } catch { return new Set(); } };
const rememberGloss = (term: string): void => { try { localStorage.setItem(GLOSS_SEEN, JSON.stringify([...new Set([...glossSeen(), term])])); } catch { /* per-viewer convenience only */ } };

/**
 * One line of plain words under a KPI's name at its first use on this device (F16.5 I): the glossary's own `plain` sentence (the first sentence, never a second table of words), then it steps
 * aside: the dotted term keeps the long form. `extra` adds a clause for a figure with a basis of its own ("run rate at month 12" on a single-month margin).
 */
export function GxFirstGloss({ term, extra }: { readonly term: string; readonly extra?: string }) {
  const entry = useGlossaryEntry(term);
  const [first] = useState(() => !glossSeen().has(term));
  useEffect(() => { if (first && entry !== undefined) rememberGloss(term); }, [first, entry, term]);
  if (!first || entry === undefined) return extra === undefined ? null : <small className="gx-faint gx-gloss" data-testid={`gloss.${term}.extra`}>{extra}</small>;
  const sentence = /^.*?[.!?](\s|$)/.exec(entry.plain)?.[0].trim() ?? entry.plain;
  return <small className="gx-faint gx-gloss" data-testid={`gloss.${term}`}>{sentence}{extra === undefined ? "" : ` ${extra}`}</small>;
}
